import authSessionService from 'services/authSession';

import { ACCOUNT_PARTNER, ACCOUNT_ENV } from '../settings';
import apiService from './api';

type Subscribable<T> = {
  subscribe: (observer: { next: (value: T) => void; error: (err: unknown) => void }) => unknown;
};

const subscribeToPromise = <T>(source: Subscribable<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    source.subscribe({ next: resolve, error: reject });
  });

export class LoginService {
  private static readonly EDUZZ_LOAD_MAX_ATTEMPTS = 20;

  private static readonly EDUZZ_LOAD_RETRY_MS = 250;

  private loggingIn = false;

  private eduzzLoadAttempts = 0;

  public auth() {
    if (this.loggingIn || authSessionService.isLoggingOut()) {
      return;
    }

    this.loggingIn = true;

    apiService.resetSessionForAuth();

    const refreshToken = window.sessionStorage.getItem('refreshToken');

    if (refreshToken) {
      void this.parseAuth(
        apiService.post('/auth_v2/refresh', { jwt_refresh_token: refreshToken }).then(response => response?.data),

        () => this.handleRefreshError()
      );

      return;
    }

    const eduzzLogin = (window as any).Eduzz?.Accounts?.login;

    if (!eduzzLogin) {
      this.loggingIn = false;

      if (this.eduzzLoadAttempts < LoginService.EDUZZ_LOAD_MAX_ATTEMPTS) {
        this.eduzzLoadAttempts += 1;

        window.setTimeout(() => this.auth(), LoginService.EDUZZ_LOAD_RETRY_MS);
      }

      return;
    }

    this.eduzzLoadAttempts = 0;

    const newLogin = eduzzLogin.call(
      (window as any).Eduzz.Accounts,
      ACCOUNT_PARTNER,
      ACCOUNT_ENV
    ) as Subscribable<string>;

    void this.parseAuth(
      subscribeToPromise(newLogin).then(token =>
        apiService.post('/auth_v2/login', { token }).then(response => response?.data)
      )
    );
  }

  public logout() {
    authSessionService.clearSession();
  }

  private handleRefreshError(): void {
    try {
      authSessionService.clearSession();
    } catch {}

    this.loggingIn = false;

    this.auth();
  }

  private async parseAuth(
    step: Promise<{ jwt_token: string; jwt_refresh_token: string } | undefined>,
    onError?: (err: unknown) => void
  ) {
    try {
      const data = await step;

      if (!data?.jwt_token) {
        this.loggingIn = false;

        return;
      }

      authSessionService.setTokens({
        token: data.jwt_token,

        refresh: data.jwt_refresh_token
      });

      window.history.pushState(null, null, '/');

      this.loggingIn = false;
    } catch (err) {
      if (onError) {
        onError(err);

        return;
      }

      this.loggingIn = false;
    }
  }
}

const loginService = new LoginService();

export default loginService;
